import React, { useState } from 'react'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Heading,
  Link,
  Image,
  Text,
  useColorModeValue,
} from '@chakra-ui/react'
import ReactMarkDown from 'react-markdown'

import { ISlide } from '@/types/strapi/commonComponents.interface'

type Props = {
  title: string
  subtitle?: string
  slideshow: ISlide[]
  reverseLayout?: boolean
  alignStart?: boolean
}

export const VerticalSlideshow = ({
  title,
  subtitle,
  slideshow,
  reverseLayout = false,
  alignStart = false,
}: Props) => {
  const [activeSlide, setActiveSlide] = useState(0)
  const secondaryColor = useColorModeValue('blackAlpha.900', 'whiteAlpha.900')
  const headingColor = useColorModeValue('blackAlpha.800', 'whiteAlpha.800')
  const frameColor = useColorModeValue('black', 'gray.890')

  return (
    <>
      <Heading
        variant="spaceGrotesk"
        as="h2"
        fontSize="40px"
        textAlign={alignStart ? 'start' : 'center'}
        fontWeight={300}
      >
        {title}
      </Heading>
      {subtitle && (
        <Heading
          variant="spaceGrotesk"
          fontSize="24px"
          fontWeight={300}
          as="h4"
          textAlign={alignStart ? 'start' : 'center'}
          mt={4}
          color={headingColor}
        >
          {subtitle}
        </Heading>
      )}
      <Flex
        direction={reverseLayout ? 'row-reverse' : 'row'}
        mt={9}
        alignItems="center"
        gap="64px"
      >
        <Box display={{ base: 'none', lg: 'block' }} flex="1" minWidth={0}>
          {slideshow.map((slide: ISlide, index: number) => (
            <Box
              key={slide.image.documentId ?? `slide-image-${index}`}
              display={activeSlide === index ? 'block' : 'none'}
              bg={slide.hasFrame ? frameColor : undefined}
              p={slide.hasFrame ? '48px' : undefined}
            >
              <Image
                rounded="xl"
                width="100%"
                objectFit="cover"
                src={slide.image.url}
                alt={slide.image.alternativeText || slide.title}
              />
            </Box>
          ))}
        </Box>
        <Accordion
          flex="1"
          minWidth={0}
          defaultIndex={[0]}
          onChange={(activeSlide: number) => {
            setActiveSlide(activeSlide)
          }}
        >
          {slideshow.map((slide: ISlide, index: number) => (
            <AccordionItem
              key={slide.id}
              borderTopWidth={index === 0 ? 0 : '1px'}
              borderColor={secondaryColor}
              borderBottom={0}
              pt={index === 0 ? 0 : 8}
              pb={8}
            >
              <h2>
                <AccordionButton px={0}>
                  <Box
                    className="font-space-grotesk"
                    fontSize="2xl"
                    fontWeight={500}
                    as="span"
                    flex="1"
                    textAlign="left"
                  >
                    {slide.title}
                  </Box>
                  <AccordionIcon fontSize="2xl" />
                </AccordionButton>
              </h2>
              <AccordionPanel
                pb={0}
                color={secondaryColor}
                fontWeight={300}
                px={0}
              >
                <ReactMarkDown className="markdown">
                  {slide.description}
                </ReactMarkDown>
                {slide.link && (
                  <Box mt={2}>
                    <Text
                      as={Link}
                      variant="brandGradient"
                      href={slide.link.url}
                      isExternal={slide.link.newTab}
                      fontSize="18px"
                      fontWeight={600}
                    >
                      {slide.link.text}
                    </Text>
                  </Box>
                )}
                <Box
                  display={{ base: 'block', lg: 'none' }}
                  mt={4}
                  bg={slide.hasFrame ? frameColor : undefined}
                  p={slide.hasFrame ? '36px' : undefined}
                >
                  <Image
                    rounded="xl"
                    key={
                      slide.imageMobile.documentId ?? `slide-mobile-${index}`
                    }
                    src={slide.imageMobile.url}
                    alt={slide.imageMobile.alternativeText || slide.title}
                  />
                </Box>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Flex>
    </>
  )
}
